import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { FicheDocument } from './PipelineDocuments';

/**
 * Aperçu du devis signé, affiché DIRECTEMENT dans le panneau de l'étape « Valider le devis signé », à la
 * place de la phrase d'explication — plus de modale, plus de lien vers une autre section (demande d'Emeni,
 * 2026-09-02 : « la page est trop bordélique, on ne doit pas aller chercher l'information ailleurs »).
 *
 * Le fichier est servi par la même route authentifiée que la section Documents (le jeton est nécessaire,
 * un lien nu ne suffirait pas) ; l'aperçu affiche l'image ou le PDF inline, avec un repli en téléchargement
 * pour tout autre type de fichier.
 */
export function DevisSigneApercu({ dossierId, document: doc }: { dossierId: string; document: FicheDocument }) {
    const [chargement, setChargement] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [url, setUrl] = React.useState('');

    React.useEffect(() => {
        let annule = false;
        const token = localStorage.getItem('token');
        fetch(`/api/suivi-privatisation/${dossierId}/documents/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => { if (!res.ok) throw new Error('Document introuvable.'); return res.blob(); })
            .then((blob) => { if (!annule) setUrl(URL.createObjectURL(blob)); })
            .catch((e: any) => { if (!annule) setErreur(e?.message || "L'aperçu n'a pas pu être chargé."); })
            .finally(() => { if (!annule) setChargement(false); });
        return () => { annule = true; if (url) URL.revokeObjectURL(url); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- ne recharger que si le document change
    }, [dossierId, doc.id]);

    const estImage = (doc.mime_type || '').startsWith('image/');
    const estPdf = (doc.mime_type || '').includes('pdf');

    return (
        <div className="rounded-xl border border-border bg-surface-soft overflow-hidden">
            <p className="text-[10px] text-content-muted px-3 pt-2">{doc.file_name}</p>
            <div className="flex items-center justify-center min-h-[220px] max-h-[45vh] overflow-auto p-2">
                {chargement ? (
                    <Loader2 className="animate-spin text-content-muted" size={22} />
                ) : erreur ? (
                    <p className="text-xs text-red-500 font-semibold flex items-center gap-1.5 p-4"><AlertCircle size={13} /> {erreur}</p>
                ) : estImage ? (
                    <img src={url} alt={doc.file_name} className="max-w-full max-h-[42vh] object-contain rounded-lg" />
                ) : estPdf ? (
                    <iframe src={url} title={doc.file_name} className="w-full h-[42vh] rounded-lg" />
                ) : (
                    <p className="text-xs text-content-muted p-4 text-center">
                        Aperçu indisponible pour ce type de fichier — le document reste accessible dans la section Documents plus bas.
                    </p>
                )}
            </div>
        </div>
    );
}
